// Inactive / Non-Working asset stock — lifecycle management without
// deleting history (repair, return to active, transfer, disposal).
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PackageX, Wrench, RotateCcw, ArrowRightLeft, Recycle, Gavel, ArchiveRestore, History } from "lucide-react";
import { GovFooterNote, useLocalBodyName } from "./shared";

const ACTIONS = [
  { id: "repair", label: "Send for Repair", icon: Wrench, cls: "bg-amber-500/10 text-amber-700 border-amber-200" },
  { id: "return_active", label: "Return to Active", icon: RotateCcw, cls: "bg-emerald-500/10 text-emerald-700 border-emerald-200" },
  { id: "transfer", label: "Transfer", icon: ArrowRightLeft, cls: "bg-brand-blue/10 text-brand-blue border-brand-blue/30" },
  { id: "dispose_ewaste", label: "E-Waste Disposal", icon: Recycle, cls: "bg-red-500/10 text-red-700 border-red-200" },
  { id: "dispose_auction", label: "Auction", icon: Gavel, cls: "bg-red-500/10 text-red-700 border-red-200" },
  { id: "dispose_other", label: "Other Disposal", icon: PackageX, cls: "bg-slate-500/10 text-slate-700 border-slate-200" },
];

const STATUS_CLS = {
  "Inactive / Non-Working": "bg-amber-500/10 text-amber-700 border-amber-200",
  "Under Repair": "bg-brand-blue/10 text-brand-blue border-brand-blue/30",
  "Transferred": "bg-slate-500/10 text-slate-700 border-slate-200",
  "Disposed (E-Waste)": "bg-red-500/10 text-red-700 border-red-200",
  "Disposed (Auction)": "bg-red-500/10 text-red-700 border-red-200",
  "Disposed (Other)": "bg-red-500/10 text-red-700 border-red-200",
};

export default function InactiveStockPanel() {
  const localBody = useLocalBodyName();
  const [items, setItems] = useState([]);
  const [trash, setTrash] = useState([]);
  const [showAllTrash, setShowAllTrash] = useState(false);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // { item, action }
  const [note, setNote] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [historyOf, setHistoryOf] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: hw }, { data: tr }] = await Promise.all([
        api.get("/hardware"),
        api.get("/hardware/trash/list"),
      ]);
      setItems(hw || []);
      setTrash(tr || []);
    } catch {
      toast.error("Failed to load inactive stock");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const inactive = useMemo(
    () => items.filter(it => (it.status || "Active") !== "Active"),
    [items]
  );

  const runAction = async () => {
    if (!action) return;
    try {
      await api.post(`/hardware/${action.item.id}/lifecycle`, {
        action: action.action.id,
        note, transfer_to: transferTo,
      });
      toast.success(`${action.action.label} recorded`);
      setAction(null); setNote(""); setTransferTo("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const restore = async (item) => {
    try {
      await api.post(`/hardware/${item.id}/restore`);
      toast.success(`Restored ${item.asset_id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="inactive-stock-panel">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg sm:text-xl font-bold tracking-wide">INACTIVE STOCK</h2>
            <p className="text-sm mt-1"><span className="font-semibold">Name of Local Body:</span> {localBody || "—"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Non-working assets move here. History (complaints, services, assignments, status) is always preserved.
            </p>
          </div>
          <Badge variant="outline" className="font-mono text-[11px] px-2.5 py-1 border-foreground/30">Format No: IKM/TSIM/FMT/29</Badge>
        </div>
      </Card>

      {loading ? (
        <Card className="p-10 text-center text-muted-foreground">Loading…</Card>
      ) : !inactive.length ? (
        <Card className="p-10 text-center" data-testid="inactive-empty">
          <PackageX className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-semibold">No inactive assets</h3>
          <p className="text-sm text-muted-foreground mt-1">Mark an asset as Inactive / Non-Working from the Hardware Register to move it here.</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {inactive.map((it, i) => (
            <Card key={it.id} className="p-4" data-testid={`inactive-item-${i}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-brand-blue font-bold">{it.asset_id}</p>
                  <p className="font-semibold mt-0.5">{it.item_name || "Untitled Item"}</p>
                  <p className="text-xs text-muted-foreground">{[it.subtype || it.type_of_instrument, it.brand, it.serial_no].filter(Boolean).join(" · ")}</p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className={`inline-block text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${STATUS_CLS[it.status] || "bg-slate-500/10 text-slate-700 border-slate-200"}`} data-testid={`inactive-status-${i}`}>
                      {it.status}
                    </span>
                    {(it.status_history || []).length > 0 && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setHistoryOf(it)} data-testid={`inactive-history-${i}`}>
                        <History className="w-3.5 h-3.5 mr-1" /> History ({it.status_history.length})
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ACTIONS.map(a => (
                    <Button key={a.id} variant="outline" size="sm" className={`h-8 text-xs border ${a.cls}`}
                      onClick={() => { setAction({ item: it, action: a }); setNote(""); setTransferTo(""); }}
                      data-testid={`inactive-${a.id}-${i}`}>
                      <a.icon className="w-3.5 h-3.5 mr-1" /> {a.label}
                    </Button>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {trash.length > 0 && (
        <Card className="p-5" data-testid="trash-panel">
          <h3 className="font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">Deleted Assets (history preserved — restorable)</h3>
          <div className="space-y-2">
            {(showAllTrash ? trash : trash.slice(0, 5)).map((it, i) => {
              const idReused = items.some(x => x.asset_id === it.asset_id);
              return (
              <div key={it.id} className="flex items-center justify-between gap-3 border border-border rounded-lg p-3" data-testid={`trash-item-${i}`}>
                <div>
                  <span className="font-mono text-xs font-bold">{it.asset_id}</span>
                  <span className="text-sm ml-2">{it.item_name}</span>
                  <span className="text-xs text-muted-foreground ml-2">deleted {String(it.deleted_at || "").slice(0, 10)}</span>
                </div>
                {idReused ? (
                  <span className="text-xs text-muted-foreground italic" data-testid={`trash-reused-${i}`}>Asset ID reused — cannot restore</span>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => restore(it)} data-testid={`trash-restore-${i}`}>
                    <ArchiveRestore className="w-4 h-4 mr-1.5" /> Restore
                  </Button>
                )}
              </div>
              );
            })}
            {trash.length > 5 && (
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowAllTrash(v => !v)} data-testid="trash-toggle">
                {showAllTrash ? "Show fewer" : `Show all (${trash.length})`}
              </Button>
            )}
          </div>
        </Card>
      )}
      <GovFooterNote />

      {/* Lifecycle action dialog */}
      <Dialog open={!!action} onOpenChange={v => !v && setAction(null)}>
        <DialogContent className="max-w-md" data-testid="lifecycle-dialog">
          <DialogHeader>
            <DialogTitle>{action?.action.label}</DialogTitle>
            <DialogDescription>
              {action?.item.asset_id} · {action?.item.item_name} — current status: {action?.item.status || "Active"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {action?.action.id === "transfer" && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Transfer To (Local Body / Institution)</Label>
                <Input value={transferTo} onChange={e => setTransferTo(e.target.value)} className="bg-white" data-testid="lifecycle-transfer-to" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Remarks / Note</Label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} className="bg-white" data-testid="lifecycle-note" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAction(null)}>Cancel</Button>
            <Button onClick={runAction} className="bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="lifecycle-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status history dialog */}
      <Dialog open={!!historyOf} onOpenChange={v => !v && setHistoryOf(null)}>
        <DialogContent className="max-w-md" data-testid="status-history-dialog">
          <DialogHeader>
            <DialogTitle>Status History</DialogTitle>
            <DialogDescription>{historyOf?.asset_id} · {historyOf?.item_name}</DialogDescription>
          </DialogHeader>
          <ol className="relative border-l-2 border-border pl-4 space-y-3 max-h-80 overflow-y-auto">
            {(historyOf?.status_history || []).slice().reverse().map((h, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-brand-blue" />
                <div className="text-sm font-semibold">{h.from} → {h.to}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(h.at).toLocaleString("en-GB")} · {h.by}
                </div>
                {h.transfer_to && <div className="text-xs">Transferred to: {h.transfer_to}</div>}
                {h.note && <div className="text-xs text-muted-foreground">{h.note}</div>}
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
}
